import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { clinicDataApi, metaApi, clinicorpApi } from '../services/api';
import useAuth from '../hooks/useAuth';

const DAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

function formatDateBR(iso) {
  if (!iso) return '-';
  // periodStart/periodEnd are calendar-day markers stored at UTC midnight —
  // format in UTC so Brazil (UTC-3) doesn't roll the date back to the previous day.
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' });
}

function formatDateTimeBR(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function accountNames(routine, accounts) {
  const ids = [routine.adAccountId, ...(routine.extraAdAccountIds || [])];
  return ids.map(id => accounts.find(a => a.id === id)?.name || id).join(', ');
}

function formatPeriod(col) {
  if (!col) return '-';
  const s = formatDateBR(col.periodStart);
  const e = formatDateBR(col.periodEnd);
  if (col.periodType === 'DAILY') return s;
  return `${s} a ${e}`;
}

const EMPTY_FORM = {
  name: '', adAccountId: '', extraAdAccountIds: [], pageId: '', pageName: '',
  whatsappNumber: '', remindersEnabled: true, scheduleType: 'DAILY', scheduleHour: 8, scheduleDay: 1,
  clinicorpAccountId: '',
};

const EMPTY_ENTRY = { leads: '', appointments: '', attendances: '', closings: '', notes: '' };

function isoDateNDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

const getDefaultRange = () => ({ startDate: isoDateNDaysAgo(30), endDate: isoDateNDaysAgo(0) });

export default function ClinicData() {
  const { user } = useAuth();
  const [routines, setRoutines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [pages, setPages] = useState([]);
  const [clinicorpAccounts, setClinicorpAccounts] = useState([]);

  // Form modal state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Collections panel state
  const [activeRoutineId, setActiveRoutineId] = useState(null);
  const [collections, setCollections] = useState([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsPage, setCollectionsPage] = useState(1);
  const [collectionsTotal, setCollectionsTotal] = useState(0);
  const COLLECTIONS_LIMIT = 30;

  // Period filter + unit summary
  const [range, setRange] = useState(getDefaultRange);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Action states
  const [quickRegisteringKey, setQuickRegisteringKey] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  // Date trigger state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [triggerDate, setTriggerDate] = useState('');
  const [triggeringDate, setTriggeringDate] = useState(false);

  // Manual entry modal
  const [manualModal, setManualModal] = useState(null); // { collectionId, current entry or null }
  const [manualEntry, setManualEntry] = useState(EMPTY_ENTRY);
  const [savingManual, setSavingManual] = useState(false);

  const loadRoutines = useCallback(async () => {
    try {
      const res = await clinicDataApi.getRoutines();
      setRoutines(res.data || []);
    } catch { toast.error('Erro ao carregar rotinas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadRoutines();
    metaApi.getAccounts().then(r => setAccounts(r.data?.accounts || [])).catch(() => {});
    metaApi.getPages().then(r => setPages(r.data?.pages || [])).catch(() => {});
    clinicorpApi.getAccounts().then(r => setClinicorpAccounts(r.data?.accounts || [])).catch(() => {});
  }, [loadRoutines]);

  const loadCollections = useCallback(async (routineId, page = 1, r = range) => {
    setCollectionsLoading(true);
    try {
      const res = await clinicDataApi.getCollections(routineId, page, COLLECTIONS_LIMIT, r.startDate, r.endDate);
      setCollections(res.collections || []);
      setCollectionsTotal(res.total || 0);
      setCollectionsPage(page);
    } catch { toast.error('Erro ao carregar coletas'); }
    finally { setCollectionsLoading(false); }
  }, [range]);

  const loadSummary = useCallback(async (routineId, r = range) => {
    setSummaryLoading(true);
    try {
      const res = await clinicDataApi.getRoutineSummary(routineId, r.startDate, r.endDate);
      setSummary(res.data || null);
    } catch { setSummary(null); }
    finally { setSummaryLoading(false); }
  }, [range]);

  const openPanel = (routineId) => {
    if (activeRoutineId === routineId) { setActiveRoutineId(null); return; }
    setActiveRoutineId(routineId);
    setCollections([]);
    setSummary(null);
    setShowDatePicker(false);
    setTriggerDate('');
    // Recompute on every open — a module-level constant goes stale if the tab
    // stays open past midnight, pinning the filter to the day it was loaded.
    const freshRange = getDefaultRange();
    setRange(freshRange);
    loadCollections(routineId, 1, freshRange);
    loadSummary(routineId, freshRange);
  };

  const handleRangeChange = (patch) => {
    const next = { ...range, ...patch };
    setRange(next);
    if (activeRoutineId) {
      loadCollections(activeRoutineId, 1, next);
      loadSummary(activeRoutineId, next);
    }
  };

  // Routine form
  const openCreate = () => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); };
  const openEdit = (r) => {
    setForm({ name: r.name, adAccountId: r.adAccountId, extraAdAccountIds: r.extraAdAccountIds || [], pageId: r.pageId, pageName: r.pageName || '',
      whatsappNumber: r.whatsappNumber || '', remindersEnabled: r.remindersEnabled !== false, scheduleType: r.scheduleType,
      scheduleHour: r.scheduleHour, scheduleDay: r.scheduleDay ?? 1, clinicorpAccountId: r.clinicorpAccountId || '' });
    setEditingId(r.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, scheduleHour: parseInt(form.scheduleHour, 10) };
      if (editingId) { await clinicDataApi.updateRoutine(editingId, payload); toast.success('Rotina atualizada'); }
      else { await clinicDataApi.createRoutine(payload); toast.success('Rotina criada'); }
      setShowForm(false);
      loadRoutines();
    } catch (err) { toast.error(err.message || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Remover esta rotina e todo histórico?')) return;
    try { await clinicDataApi.deleteRoutine(id); toast.success('Rotina removida'); setRoutines(p => p.filter(r => r.id !== id)); if (activeRoutineId === id) setActiveRoutineId(null); }
    catch { toast.error('Erro ao remover'); }
  };

  // Quick register from the collapsed card: "Hoje" or "Ontem" — creates/fetches the
  // period collection (dispatching the WhatsApp webhook too, if the routine has one
  // configured) and immediately opens the fill modal for it, metrics already loaded.
  const handleQuickRegister = async (routineId, which) => {
    const key = `${routineId}:${which}`;
    setQuickRegisteringKey(key);
    try {
      const dateStr = isoDateNDaysAgo(which === 'today' ? 0 : 1);
      const res = await clinicDataApi.triggerRoutineForDate(routineId, dateStr);
      const col = res.data?.collection;
      if (col) openManual(col);
      if (res.data?.webhookSuccess) toast.success('Coleta registrada e formulário enviado!');
      if (activeRoutineId === routineId) { loadCollections(routineId, collectionsPage); loadSummary(routineId); }
      loadRoutines();
    } catch (err) { toast.error(err.message || 'Erro ao registrar período'); }
    finally { setQuickRegisteringKey(null); }
  };

  const handleTriggerDate = async (routineId) => {
    if (!triggerDate) return;
    setTriggeringDate(true);
    try {
      const res = await clinicDataApi.triggerRoutineForDate(routineId, triggerDate);
      if (res.data?.webhookSuccess === null) toast.success(res.data?.existed ? 'Período já registrado' : 'Período registrado');
      else if (res.data?.existed) toast('Coleta existente para esse período — webhook reenviado');
      else if (res.data?.webhookSuccess) toast.success('Coleta criada e formulário enviado!');
      else toast.error(`Webhook retornou erro: ${res.data?.webhookStatus || '?'}`);
      setShowDatePicker(false);
      setTriggerDate('');
      loadCollections(routineId, 1);
      loadSummary(routineId);
    } catch (err) { toast.error(err.message || 'Erro'); }
    finally { setTriggeringDate(false); }
  };

  const handleResend = async (collectionId) => {
    setResendingId(collectionId);
    try {
      const res = await clinicDataApi.resendWebhook(collectionId);
      if (res.data?.webhookSuccess) toast.success('Webhook reenviado com sucesso!');
      else toast.error(`Falha no webhook: ${res.data?.webhookStatus || '?'}`);
      if (activeRoutineId) loadCollections(activeRoutineId, collectionsPage);
    } catch { toast.error('Erro ao reenviar'); }
    finally { setResendingId(null); }
  };

  const openManual = (col) => {
    setManualEntry(col.entry
      ? { leads: String(col.entry.leads), appointments: String(col.entry.appointments), attendances: String(col.entry.attendances), closings: String(col.entry.closings ?? 0), notes: col.entry.notes || '' }
      : EMPTY_ENTRY);
    setManualModal(col);
  };

  const handleManualSave = async () => {
    if (!manualModal) return;
    setSavingManual(true);
    try {
      await clinicDataApi.manualFill(manualModal.id, {
        leads: parseInt(manualEntry.leads) || 0,
        appointments: parseInt(manualEntry.appointments) || 0,
        attendances: parseInt(manualEntry.attendances) || 0,
        closings: parseInt(manualEntry.closings) || 0,
        notes: manualEntry.notes,
      });
      toast.success('Dados salvos manualmente');
      setManualModal(null);
      if (activeRoutineId) { loadCollections(activeRoutineId, collectionsPage); loadSummary(activeRoutineId); }
    } catch { toast.error('Erro ao salvar'); }
    finally { setSavingManual(false); }
  };

  const scheduleLabel = (r) => {
    const h = String(r.scheduleHour).padStart(2, '0') + ':00';
    if (r.scheduleType === 'DAILY') return `Diário às ${h}`;
    return `Semanal toda ${DAYS[r.scheduleDay ?? 0]} às ${h}`;
  };

  const activeRoutine = routines.find(r => r.id === activeRoutineId);
  const totalPages = Math.ceil(collectionsTotal / COLLECTIONS_LIMIT);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Dados das Clínicas</h1>
          <p className="text-sm text-slate-500 mt-1">Rotinas de coleta manual de dados de conversão</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors">
          <span className="material-symbols-outlined text-[18px]">add</span>Nova Rotina
        </button>
      </div>

      {/* Routine Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-slate-800">{editingId ? 'Editar Rotina' : 'Nova Rotina'}</h2>
              <button onClick={() => setShowForm(false)}><span className="material-symbols-outlined text-slate-400">close</span></button>
            </div>
            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              <Field label="Nome da Rotina *">
                <input required value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="input w-full" placeholder="Ex: Clínica Centro - Diário" />
              </Field>
              <Field label="Conta de Anúncio *">
                <select required value={form.adAccountId} onChange={e => setForm(p => ({ ...p, adAccountId: e.target.value, extraAdAccountIds: p.extraAdAccountIds.filter(id => id !== e.target.value) }))} className="input w-full">
                  <option value="">Selecione...</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </Field>
              <Field label="Contas de Anúncio adicionais (opcional)">
                <select multiple value={form.extraAdAccountIds}
                  onChange={e => setForm(p => ({ ...p, extraAdAccountIds: Array.from(e.target.selectedOptions, o => o.value) }))}
                  className="input w-full h-28">
                  {accounts.filter(a => a.id !== form.adAccountId).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <p className="text-[11px] text-slate-400 mt-1.5">Ctrl/Cmd + clique para selecionar mais de uma. Só soma "conversas iniciadas" — leads/agendamentos continuam vindo do preenchimento manual.</p>
              </Field>
              <Field label="Página Vinculada *">
                <select value={form.pageId} onChange={e => {
                  const pg = pages.find(p => p.id === e.target.value);
                  setForm(p => ({ ...p, pageId: e.target.value, pageName: pg?.name || '' }));
                }} className="input w-full">
                  <option value="">Selecione...</option>
                  {pages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {!pages.length && (
                  <input value={form.pageId} onChange={e => setForm(p => ({ ...p, pageId: e.target.value }))} className="input w-full mt-2" placeholder="ID da Página (manual)" />
                )}
              </Field>
              <Field label="Conta Clinicorp (opcional)">
                <select value={form.clinicorpAccountId} onChange={e => setForm(p => ({ ...p, clinicorpAccountId: e.target.value }))} className="input w-full">
                  <option value="">Nenhuma — preenchimento manual</option>
                  {clinicorpAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
                <p className="text-[11px] text-slate-400 mt-1.5">Quando selecionada, agendamentos/comparecimentos/fechamentos/leads são preenchidos automaticamente todo dia a partir do Clinicorp — sem precisar do formulário manual. Uma correção manual em um dia específico sempre prevalece sobre o automático.</p>
              </Field>
              <label className="flex items-center gap-2.5 bg-slate-50 border border-slate-100 rounded-xl px-3.5 py-3 cursor-pointer">
                <input type="checkbox" checked={form.remindersEnabled} onChange={e => setForm(p => ({ ...p, remindersEnabled: e.target.checked }))}
                  className="w-4 h-4 rounded accent-blue-600" />
                <div>
                  <p className="text-sm font-semibold text-slate-700">Enviar lembrete automático via WhatsApp</p>
                  <p className="text-[11px] text-slate-400">Desative para preencher os dados direto pela tabela, sem disparo de WhatsApp.</p>
                </div>
              </label>
              {form.remindersEnabled && (
                <>
                  <Field label="Destinatário (WhatsApp ou Grupo) *">
                    <input required value={form.whatsappNumber} onChange={e => setForm(p => ({ ...p, whatsappNumber: e.target.value }))} className="input w-full" placeholder="5511999999999 ou 120363341563801101@g.us" />
                    <p className="text-[11px] text-slate-400 mt-1.5">Número individual (ex: 5511999999999) ou ID de grupo do WhatsApp (termina em @g.us)</p>
                  </Field>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Frequência *">
                      <select value={form.scheduleType} onChange={e => setForm(p => ({ ...p, scheduleType: e.target.value }))} className="input w-full">
                        <option value="DAILY">Diária</option>
                        <option value="WEEKLY">Semanal</option>
                      </select>
                    </Field>
                    <Field label="Horário de Envio *">
                      <select value={form.scheduleHour} onChange={e => setForm(p => ({ ...p, scheduleHour: parseInt(e.target.value) }))} className="input w-full">
                        {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>)}
                      </select>
                    </Field>
                  </div>
                  {form.scheduleType === 'WEEKLY' && (
                    <Field label="Dia da Semana *">
                      <select value={form.scheduleDay} onChange={e => setForm(p => ({ ...p, scheduleDay: parseInt(e.target.value) }))} className="input w-full">
                        {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                      </select>
                    </Field>
                  )}
                </>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 btn-secondary">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-2.5 rounded-xl transition-colors">
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {manualModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="font-bold text-slate-800">Preenchimento Manual</h2>
                <span className="inline-flex items-center gap-1.5 mt-1.5 bg-orange-500 text-white text-sm font-bold px-3 py-1 rounded-lg">
                  <span className="material-symbols-outlined text-[16px]">calendar_today</span>
                  {formatPeriod(manualModal)}
                </span>
              </div>
              <button onClick={() => setManualModal(null)}><span className="material-symbols-outlined text-slate-400">close</span></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {manualModal.entry && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                  <span className="material-symbols-outlined text-amber-500 text-[16px]">edit_note</span>
                  <p className="text-xs text-amber-700">Editando dados existentes{manualModal.entry.isManualEntry ? ' (manual anterior)' : ' (preenchido pelo responsável)'}.</p>
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl p-2.5 bg-amber-50 text-amber-700">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="material-symbols-outlined text-[13px]">chat</span>
                    <p className="text-[9px] font-medium uppercase tracking-wider opacity-70 leading-tight">Conversas</p>
                  </div>
                  <p className="text-base font-bold">{manualModal.conversationsStarted === null || manualModal.conversationsStarted === undefined ? '—' : manualModal.conversationsStarted}</p>
                </div>
                <div className="rounded-xl p-2.5 bg-blue-50 text-blue-700">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="material-symbols-outlined text-[13px]">hub</span>
                    <p className="text-[9px] font-medium uppercase tracking-wider opacity-70 leading-tight">Leads Meta Ads</p>
                  </div>
                  <p className="text-base font-bold">{manualModal.metaAdsLeadsCount === null || manualModal.metaAdsLeadsCount === undefined ? '—' : manualModal.metaAdsLeadsCount}</p>
                </div>
                <div className="rounded-xl p-2.5 bg-slate-100 text-slate-600">
                  <div className="flex items-center gap-1 mb-1">
                    <span className="material-symbols-outlined text-[13px]">language</span>
                    <p className="text-[9px] font-medium uppercase tracking-wider opacity-70 leading-tight">Landing Page</p>
                  </div>
                  <p className="text-base font-bold">{manualModal.landingPageLeadsCount === null || manualModal.landingPageLeadsCount === undefined ? '—' : manualModal.landingPageLeadsCount}</p>
                </div>
              </div>
              <ManualField label="Leads" icon="person_add" value={manualEntry.leads} onChange={v => setManualEntry(p => ({ ...p, leads: v }))} />
              <ManualField label="Agendamentos" icon="event" value={manualEntry.appointments} onChange={v => setManualEntry(p => ({ ...p, appointments: v }))} />
              <ManualField label="Comparecimentos" icon="how_to_reg" value={manualEntry.attendances} onChange={v => setManualEntry(p => ({ ...p, attendances: v }))} />
              <ManualField label="Fechamentos" icon="task_alt" value={manualEntry.closings} onChange={v => setManualEntry(p => ({ ...p, closings: v }))} />
              <Field label="Observação (opcional)">
                <textarea value={manualEntry.notes} onChange={e => setManualEntry(p => ({ ...p, notes: e.target.value }))}
                  className="input w-full" rows={2} placeholder="Nota interna — não aparece no relatório" />
              </Field>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setManualModal(null)} className="flex-1 btn-secondary">Cancelar</button>
                <button onClick={handleManualSave} disabled={savingManual} className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold py-2.5 rounded-xl transition-colors">
                  {savingManual ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Routine List */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-24 bg-slate-100 rounded-xl animate-pulse" />)}</div>
      ) : routines.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
          <span className="material-symbols-outlined text-slate-300 text-5xl">local_hospital</span>
          <p className="mt-3 text-slate-500 font-medium">Nenhuma rotina criada</p>
        </div>
      ) : (
        <div className="space-y-3">
          {routines.map(r => {
            const isOpen = activeRoutineId === r.id;
            const lastCol = r.collections?.[0];
            return (
              <div key={r.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                {/* Routine Header Row */}
                <div className="px-5 py-4 flex items-center gap-4">
                  <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${r.filledToday ? 'bg-green-500' : 'bg-slate-300'}`}
                    title={r.filledToday ? 'Coleta lançada hoje' : 'Pendente — nenhuma coleta lançada hoje'} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{r.name}</span>
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                        {r.isActive ? 'Ativa' : 'Pausada'}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.remindersEnabled ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                        <span className="material-symbols-outlined text-[11px]">{r.remindersEnabled ? 'notifications_active' : 'edit_note'}</span>
                        {r.remindersEnabled ? 'Lembrete Automático' : 'Preenchimento Manual'}
                      </span>
                      {r.clinicorpAccountId && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-purple-100 text-purple-700" title="Dados preenchidos automaticamente a partir do Clinicorp">
                          <span className="material-symbols-outlined text-[11px]">sync</span>
                          Auto Clinicorp
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                      <span className="flex items-center gap-1" title={r.extraAdAccountIds?.length ? accountNames(r, accounts) : undefined}>
                        <span className="material-symbols-outlined text-[14px]">hub</span>{r.adAccount?.name}
                        {r.extraAdAccountIds?.length > 0 && ` +${r.extraAdAccountIds.length}`}
                      </span>
                      {r.remindersEnabled && <span className="flex items-center gap-1"><span className="material-symbols-outlined text-[14px]">schedule</span>{scheduleLabel(r)}</span>}
                      {r.whatsappNumber && (
                        <span className="flex items-center gap-1" title={r.whatsappNumber}>
                          <span className="material-symbols-outlined text-[14px]">{r.whatsappNumber?.endsWith('@g.us') ? 'group' : 'smartphone'}</span>
                          {r.whatsappNumber?.endsWith('@g.us') ? 'Grupo' : r.whatsappNumber}
                        </span>
                      )}
                      {lastCol && (
                        <span className="flex items-center gap-1">
                          <span className="material-symbols-outlined text-[14px]">{lastCol.isFilled ? 'check_circle' : lastCol.isViewed ? 'visibility' : 'pending'}</span>
                          Última: {formatPeriod(lastCol)} — {lastCol.isFilled ? 'Preenchida' : lastCol.isViewed ? 'Visualizada' : 'Pendente'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    {/* Ver coletas */}
                    <ActionBtn onClick={() => openPanel(r.id)} icon={isOpen ? 'expand_less' : 'list_alt'} title="Ver coletas" color="blue" active={isOpen} />
                    {/* Registrar: Hoje / Ontem */}
                    <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-100 rounded-xl pl-2.5 p-1 mr-1">
                      <span className="text-[9px] font-bold uppercase text-orange-400 tracking-wider">Registrar:</span>
                      <QuickRegisterBtn label="Hoje" hoverColor="orange" onClick={() => handleQuickRegister(r.id, 'today')} loading={quickRegisteringKey === `${r.id}:today`} disabled={!!quickRegisteringKey} />
                      <QuickRegisterBtn label="Ontem" hoverColor="indigo" onClick={() => handleQuickRegister(r.id, 'yesterday')} loading={quickRegisteringKey === `${r.id}:yesterday`} disabled={!!quickRegisteringKey} />
                    </div>
                    {/* Editar */}
                    <SubtleBtn onClick={() => openEdit(r)} icon="edit" title="Editar rotina" />
                    {/* Deletar */}
                    <SubtleBtn onClick={() => handleDelete(r.id)} icon="delete" title="Remover" hoverColor="red" />
                  </div>
                </div>

                {/* Collections Panel */}
                {isOpen && (
                  <div className="border-t border-slate-100">
                    {/* Panel Header */}
                    <div className="bg-slate-50 px-5 py-3 flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                          Histórico de Coletas
                          {collectionsTotal > 0 && <span className="ml-2 text-slate-400 font-normal normal-case">({collectionsTotal} total)</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Period filter */}
                        <div className="flex items-center gap-1.5">
                          <input type="date" value={range.startDate} onChange={e => handleRangeChange({ startDate: e.target.value })}
                            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          <span className="text-slate-300 text-xs">até</span>
                          <input type="date" value={range.endDate} onChange={e => handleRangeChange({ endDate: e.target.value })}
                            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        </div>
                        {/* Date trigger */}
                        {showDatePicker ? (
                          <div className="flex items-center gap-2 animate-fade-in">
                            <input type="date" value={triggerDate} onChange={e => setTriggerDate(e.target.value)}
                              max={new Date().toISOString().split('T')[0]}
                              className="text-xs border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            <button onClick={() => handleTriggerDate(r.id)} disabled={!triggerDate || triggeringDate}
                              className="text-xs bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white font-semibold px-3 py-1.5 rounded-lg transition-colors">
                              {triggeringDate ? 'Enviando...' : 'Enviar'}
                            </button>
                            <button onClick={() => { setShowDatePicker(false); setTriggerDate(''); }} className="text-slate-400 hover:text-slate-600 p-1">
                              <span className="material-symbols-outlined text-[16px]">close</span>
                            </button>
                          </div>
                        ) : (
                          <button onClick={() => setShowDatePicker(true)}
                            className="flex items-center gap-1.5 text-xs text-blue-600 border border-blue-200 hover:bg-blue-50 font-semibold px-3 py-1.5 rounded-lg transition-colors">
                            <span className="material-symbols-outlined text-[14px]">calendar_add_on</span>
                            {r.remindersEnabled ? 'Solicitar Período Específico' : 'Registrar Período'}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Resumo da unidade no período */}
                    <div className="px-5 py-4 bg-white border-b border-slate-100">
                      {summaryLoading ? (
                        <div className="h-16 bg-slate-50 rounded-xl animate-pulse" />
                      ) : summary ? (
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
                          <SummaryCard label="Leads" value={summary.leads} icon="person_add" color="blue" />
                          <SummaryCard label="Agendamentos" value={summary.appointments} icon="event" color="purple" />
                          <SummaryCard label="Comparecimentos" value={summary.attendances} icon="how_to_reg" color="teal" />
                          <SummaryCard label="Fechamentos" value={summary.closings} icon="task_alt" color="green" />
                          <SummaryCard label="Conversas Iniciadas" value={summary.conversationsStarted} icon="chat" color="amber" />
                          <SummaryCard label="Leads Meta Ads" value={summary.metaAdsLeadsCount} icon="hub" color="slate" />
                          <SummaryCard label="Leads Landing Page" value={summary.landingPageLeadsCount} icon="language" color="slate" />
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">Sem dados para o período selecionado.</p>
                      )}
                    </div>

                    {/* Collections Table */}
                    {collectionsLoading ? (
                      <div className="py-8 flex justify-center">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : collections.length === 0 ? (
                      <div className="text-center py-10">
                        <span className="material-symbols-outlined text-slate-300 text-4xl">inbox</span>
                        <p className="text-sm text-slate-400 mt-2">Nenhuma coleta ainda.</p>
                        <p className="text-xs text-slate-400 mt-1">Dispare manualmente ou aguarde o agendamento automático.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-slate-100 bg-white">
                              <th className="text-left px-5 py-2.5 text-slate-500 font-semibold">Período</th>
                              <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">Leads</th>
                              <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">Agend.</th>
                              <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">Compare.</th>
                              <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">Fechamentos</th>
                              <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">Conversas Iniciadas</th>
                              <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">Leads Meta Ads</th>
                              <th className="text-center px-3 py-2.5 text-slate-500 font-semibold">Leads Landing Page</th>
                              <th className="text-right px-5 py-2.5 text-slate-500 font-semibold">Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {collections.map(c => (
                              <tr key={c.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors">
                                <td className="px-5 py-3 font-medium text-slate-700 whitespace-nowrap">{formatPeriod(c)}</td>
                                <td className="px-3 py-3 text-center">
                                  {c.isFilled ? <span className="font-bold text-slate-800">{c.entry?.leads ?? '-'}</span> : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-3 py-3 text-center">
                                  {c.isFilled ? <span className="font-bold text-slate-800">{c.entry?.appointments ?? '-'}</span> : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-3 py-3 text-center">
                                  {c.isFilled ? <span className="font-bold text-slate-800">{c.entry?.attendances ?? '-'}</span> : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-3 py-3 text-center">
                                  {c.isFilled ? <span className="font-bold text-slate-800">{c.entry?.closings ?? '-'}</span> : <span className="text-slate-300">—</span>}
                                </td>
                                <td className="px-3 py-3 text-center">
                                  {c.conversationsStarted === null ? <span className="text-slate-300">—</span> : <span className="font-bold text-teal-700">{c.conversationsStarted}</span>}
                                </td>
                                <td className="px-3 py-3 text-center">
                                  {c.metaAdsLeadsCount === null ? <span className="text-slate-300">—</span> : <span className="font-bold text-slate-800">{c.metaAdsLeadsCount}</span>}
                                </td>
                                <td className="px-3 py-3 text-center">
                                  {c.landingPageLeadsCount === null ? <span className="text-slate-300">—</span> : <span className="font-bold text-slate-800">{c.landingPageLeadsCount}</span>}
                                </td>
                                <td className="px-5 py-3">
                                  <div className="flex items-center justify-end gap-1">
                                    {/* Reenviar webhook — só quando lembrete automático está ligado */}
                                    {r.remindersEnabled && r.whatsappNumber && (
                                      <button
                                        onClick={() => handleResend(c.id)}
                                        disabled={resendingId === c.id}
                                        title="Reenviar webhook"
                                        className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-40"
                                      >
                                        <span className="material-symbols-outlined text-[16px]">
                                          {resendingId === c.id ? 'autorenew' : 'send'}
                                        </span>
                                      </button>
                                    )}
                                    {/* Editar manualmente */}
                                    <button
                                      onClick={() => openManual(c)}
                                      title="Preencher/editar manualmente"
                                      className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    >
                                      <span className="material-symbols-outlined text-[16px]">edit_note</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-between px-5 py-3 bg-slate-50 border-t border-slate-100">
                            <p className="text-xs text-slate-400">{collectionsTotal} coletas · página {collectionsPage}/{totalPages}</p>
                            <div className="flex gap-2">
                              <button disabled={collectionsPage <= 1} onClick={() => loadCollections(r.id, collectionsPage - 1)}
                                className="text-xs px-3 py-1.5 bg-white border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition-colors">
                                ← Anterior
                              </button>
                              <button disabled={collectionsPage >= totalPages} onClick={() => loadCollections(r.id, collectionsPage + 1)}
                                className="text-xs px-3 py-1.5 bg-white border border-slate-200 rounded-lg disabled:opacity-40 hover:bg-slate-50 transition-colors">
                                Próxima →
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function ManualField({ label, icon, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</label>
      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">{icon}</span>
        <input type="number" min="0" value={value} onChange={e => onChange(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>
    </div>
  );
}

function ActionBtn({ onClick, icon, title, color, loading, active }) {
  const colors = {
    blue: 'hover:text-blue-600 hover:bg-blue-50',
    green: 'hover:text-green-600 hover:bg-green-50',
    amber: 'hover:text-amber-600 hover:bg-amber-50',
    red: 'hover:text-red-500 hover:bg-red-50',
  };
  return (
    <button onClick={onClick} disabled={loading} title={title}
      className={`p-2 text-slate-400 rounded-lg transition-colors disabled:opacity-40 ${colors[color]} ${active ? 'text-blue-600 bg-blue-50' : ''}`}>
      <span className={`material-symbols-outlined text-[18px] ${loading ? 'animate-spin' : ''}`}>{loading ? 'autorenew' : icon}</span>
    </button>
  );
}

// Toned-down icon button for lower-frequency actions (edit/delete) — thin, gray,
// only picks up color on hover so it doesn't compete with the primary actions.
function SubtleBtn({ onClick, icon, title, hoverColor = 'slate' }) {
  const hover = {
    slate: 'hover:text-slate-600',
    red: 'hover:text-red-400',
  };
  return (
    <button onClick={onClick} title={title}
      className={`p-1.5 text-slate-300 rounded-md transition-colors hover:bg-slate-50 ${hover[hoverColor]}`}>
      <span className="material-symbols-outlined text-[15px]">{icon}</span>
    </button>
  );
}

function QuickRegisterBtn({ label, onClick, loading, disabled, hoverColor = 'orange' }) {
  const hover = {
    orange: 'hover:bg-orange-500 hover:text-white hover:border-orange-500',
    indigo: 'hover:bg-indigo-500 hover:text-white hover:border-indigo-500',
  };
  return (
    <button onClick={onClick} disabled={disabled} title={`Registrar: ${label}`}
      className={`text-xs font-bold uppercase px-3.5 py-2 rounded-lg border border-transparent bg-white text-slate-600 shadow-sm transition-all hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 ${hover[hoverColor]}`}>
      {loading ? <span className="material-symbols-outlined text-[15px] animate-spin block">autorenew</span> : label}
    </button>
  );
}

function SummaryCard({ label, value, icon, color }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    teal: 'bg-teal-50 text-teal-600',
    amber: 'bg-amber-50 text-amber-700',
    green: 'bg-green-50 text-green-600',
    slate: 'bg-slate-100 text-slate-600',
  };
  return (
    <div className={`rounded-xl p-3 ${colors[color]}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="material-symbols-outlined text-[14px]">{icon}</span>
        <p className="text-[10px] font-medium uppercase tracking-wider opacity-70">{label}</p>
      </div>
      <p className="text-xl font-bold">{value === null || value === undefined ? '—' : value}</p>
    </div>
  );
}
